/**
 * Shared device-location capture for field devices (controller, zone, water
 * meter, double check).
 *
 * Strategy: try a true high-accuracy GPS fix first; if that errors or times out
 * (common on weak signal or a wifi-only iPad with no GPS chip), fall back to a
 * fast low-accuracy network fix so the tech is never left unable to save a
 * location. Every attempt is logged to telemetry so a later "GPS doesn't work"
 * report can be diagnosed from the breadcrumbs (which mode succeeded, the
 * accuracy in metres, how long it took, and the exact error codes on failure).
 */
import { logEvent } from './telemetry';
import { markLocationGranted } from './locationState';

export const HIGH_ACCURACY_GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
};

const LOW_ACCURACY_GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 12000,
  maximumAge: 30000,
};

function geoErrorName(code: number): string {
  switch (code) {
    case 1:
      return 'permission_denied';
    case 2:
      return 'position_unavailable';
    case 3:
      return 'timeout';
    default:
      return `unknown_${code}`;
  }
}

function describePosition(pos: GeolocationPosition, mode: 'high' | 'low', elapsedMs: number) {
  return {
    mode,
    elapsed_ms: Math.round(elapsedMs),
    accuracy_m: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : null,
    altitude_accuracy_m:
      pos.coords.altitudeAccuracy != null ? Math.round(pos.coords.altitudeAccuracy) : null,
    latitude: pos.coords.latitude,
    longitude: pos.coords.longitude,
    position_timestamp: pos.timestamp,
  };
}

/**
 * Capture the device's location with automatic high→low accuracy fallback and
 * full diagnostic telemetry. The caller keeps its own `navigator.geolocation`
 * presence check and its own success/error UI; this only owns the capture
 * strategy and the logging.
 *
 * @param context  short label for which device is being marked (e.g. 'zone')
 * @param onSuccess called with the first usable position (high or low accuracy)
 * @param onError  called only if BOTH accuracy modes fail
 */
export function captureDeviceLocation(
  context: string,
  onSuccess: (position: GeolocationPosition) => void,
  onError: (error: GeolocationPositionError) => void,
): void {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    void logEvent('gps_capture_unavailable', `Geolocation unavailable for ${context}`, {
      severity: 'error',
      metadata: { context },
    });
    onError({
      code: 2,
      message: 'Geolocation is not available in this browser',
      PERMISSION_DENIED: 1,
      POSITION_UNAVAILABLE: 2,
      TIMEOUT: 3,
    } as GeolocationPositionError);
    return;
  }

  const startedAt = performance.now();
  void logEvent('gps_capture_started', `GPS capture started for ${context}`, {
    metadata: { context, high_accuracy_timeout_ms: HIGH_ACCURACY_GEO_OPTIONS.timeout },
  });

  navigator.geolocation.getCurrentPosition(
    (position) => {
      markLocationGranted();
      void logEvent('gps_capture_succeeded', `GPS fix (high accuracy) for ${context}`, {
        metadata: { context, fallback: false, ...describePosition(position, 'high', performance.now() - startedAt) },
      });
      onSuccess(position);
    },
    (highError) => {
      const highElapsed = performance.now() - startedAt;
      void logEvent(
        'gps_capture_retry',
        `GPS high-accuracy failed for ${context} (${geoErrorName(highError.code)}), retrying low accuracy`,
        {
          severity: 'warning',
          metadata: {
            context,
            high_error_code: highError.code,
            high_error: geoErrorName(highError.code),
            high_error_message: highError.message,
            high_elapsed_ms: Math.round(highElapsed),
          },
        },
      );

      const retryStart = performance.now();
      navigator.geolocation.getCurrentPosition(
        (position) => {
          markLocationGranted();
          void logEvent('gps_capture_succeeded', `GPS fix (low-accuracy fallback) for ${context}`, {
            metadata: {
              context,
              fallback: true,
              high_error: geoErrorName(highError.code),
              ...describePosition(position, 'low', performance.now() - retryStart),
            },
          });
          onSuccess(position);
        },
        (lowError) => {
          void logEvent('gps_capture_failed', `GPS failed in both modes for ${context}`, {
            severity: 'error',
            metadata: {
              context,
              high_error_code: highError.code,
              high_error: geoErrorName(highError.code),
              low_error_code: lowError.code,
              low_error: geoErrorName(lowError.code),
              low_error_message: lowError.message,
              total_elapsed_ms: Math.round(performance.now() - startedAt),
            },
          });
          onError(lowError);
        },
        LOW_ACCURACY_GEO_OPTIONS,
      );
    },
    HIGH_ACCURACY_GEO_OPTIONS,
  );
}

export interface BestFix {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  converged: boolean;
  elapsedMs: number;
  samples: number;
}

const PICKER_TARGET_ACCURACY_M = 25;
const PICKER_MAX_WAIT_MS = 20000;

/**
 * Accuracy-aware "best fix" capture used to seed the map pin. Watches the
 * position stream and keeps the most accurate reading, resolving as soon as it
 * reaches PICKER_TARGET_ACCURACY_M or when PICKER_MAX_WAIT_MS elapses (using the
 * best seen so far). Resolves null only if no position is obtained at all.
 * Logs the same gps_capture_* breadcrumbs as the direct capture.
 */
export function captureBestLocation(context: string): Promise<BestFix | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      void logEvent('gps_capture_unavailable', `Geolocation unavailable for ${context}`, {
        severity: 'error',
        metadata: { context },
      });
      resolve(null);
      return;
    }
    const startedAt = performance.now();
    let best: { lat: number; lng: number; acc: number | null } | null = null;
    let count = 0;
    let done = false;
    let watchId = -1;

    void logEvent('gps_capture_started', `GPS best-fix capture started for ${context}`, {
      metadata: { context, mode: 'best', target_m: PICKER_TARGET_ACCURACY_M },
    });

    const finish = (converged: boolean) => {
      if (done) return;
      done = true;
      if (watchId !== -1) navigator.geolocation.clearWatch(watchId);
      clearTimeout(timer);
      const elapsedMs = Math.round(performance.now() - startedAt);
      if (!best) {
        void logEvent('gps_capture_failed', `GPS best-fix capture got nothing for ${context}`, {
          severity: 'error',
          metadata: { context, samples: count, elapsed_ms: elapsedMs },
        });
        resolve(null);
        return;
      }
      markLocationGranted();
      void logEvent('gps_capture_succeeded', `GPS best fix for ${context}`, {
        metadata: {
          context,
          mode: 'best',
          converged,
          accuracy_m: best.acc,
          samples: count,
          elapsed_ms: elapsedMs,
          latitude: best.lat,
          longitude: best.lng,
        },
      });
      resolve({ latitude: best.lat, longitude: best.lng, accuracy: best.acc, converged, elapsedMs, samples: count });
    };

    const timer = setTimeout(() => finish(false), PICKER_MAX_WAIT_MS);

    watchId = navigator.geolocation.watchPosition(
      (pos) => {
        count++;
        const acc = pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : null;
        if (best === null || (acc != null && (best.acc == null || acc < best.acc))) {
          best = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc };
        }
        if (acc != null && acc <= PICKER_TARGET_ACCURACY_M) finish(true);
      },
      (err) => {
        if (count === 0 && !done) {
          done = true;
          if (watchId !== -1) navigator.geolocation.clearWatch(watchId);
          clearTimeout(timer);
          void logEvent('gps_capture_failed', `GPS best-fix capture error for ${context}`, {
            severity: 'error',
            metadata: { context, error_code: err.code, error: geoErrorName(err.code), error_message: err.message },
          });
          resolve(null);
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: PICKER_MAX_WAIT_MS },
    );
  });
}
